import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SessionStorageCache from 'core/utils/cache/sessionStorage';
import BottomSticky from 'core/modules/BottomSticky/BottomSticky';
import FullScreen from 'core/modules/FullScreen/FullScreen';
import SignInNagSmall from 'org/modules/SignInNagSmall/SignInNagSmall';
import SignInNagLarge from 'org/modules/SignInNagLarge/SignInNagLarge';
import { CSSTransition } from 'react-transition-group';
import { selectUserIsIdentified } from 'core/selectors/user';
import { selectSiteUrl } from 'core/selectors/app';

import {
  selectHomeSignInNagShowSmallSignInNag,
  selectHomeSignInNagShowLargeSignInNag,
  selectHomeSignInNagRenderLargeSignInNagWithAnimation,
} from './selectors/homeSignInNag';
import {
  openLargeNagWithAnimation,
  closeLargeNagAndOpenSmall,
  showTerms,
} from './actions';

const sessionStorage = new SessionStorageCache();

function HomeSignInNag({ forceShowSmallSignInOnly }) {
  const siteUrl = useSelector(selectSiteUrl);
  const showSmallSignInNag = useSelector(selectHomeSignInNagShowSmallSignInNag);
  const showLargeSignInNag = useSelector(selectHomeSignInNagShowLargeSignInNag);
  const renderLargeSignInNagWithAnimation =
    useSelector(selectHomeSignInNagRenderLargeSignInNagWithAnimation);
  const userIsIdentified = useSelector(selectUserIsIdentified);
  const dispatch = useDispatch();

  const showLargeSignInNagWithAnimation = () => {
    dispatch(openLargeNagWithAnimation());
    // eslint-disable-next-line no-use-before-define
    window.removeEventListener('scroll', handleScroll);
  };

  const handleScroll = () => {
    if (!sessionStorage.getItem('sawSignInNagLarge')) {
      const { pageYOffset } = window;

      if (pageYOffset > 50) {
        showLargeSignInNagWithAnimation();
        window.removeEventListener('scroll', handleScroll);
      }
    } else {
      window.removeEventListener('scroll', handleScroll);
    }
  };

  const renderLargeNag = () => {
    if (renderLargeSignInNagWithAnimation) {
      return (
        <CSSTransition
          in
          appear
          timeout={2000}
          classNames="mn_slideup"
        >
          <FullScreen>
            <SignInNagLarge handleClose={() => dispatch(closeLargeNagAndOpenSmall())} />
          </FullScreen>
        </CSSTransition>
      );
    }

    return (
      <FullScreen>
        <SignInNagLarge
          handleClose={() => dispatch(closeLargeNagAndOpenSmall())}
          scrollToTerms={() => dispatch(showTerms())}
        />
      </FullScreen>
    );
  };

  useEffect(() => {
    if (forceShowSmallSignInOnly) {
      return;
    }

    if (!sessionStorage.getItem('sawSignInNagLarge')) {
      window.addEventListener('scroll', handleScroll);
    } else {
      dispatch(closeLargeNagAndOpenSmall());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTermsClick = () => {
    if (forceShowSmallSignInOnly) {
      window.open(`${siteUrl}/signin`, '_self');
      return;
    }

    dispatch(showTerms());
  };

  return (
    <>
      {!userIsIdentified && (showSmallSignInNag || forceShowSmallSignInOnly) &&
        <BottomSticky>
          <SignInNagSmall handleTermsClick={handleTermsClick} />
        </BottomSticky>
      }
      {!userIsIdentified && showLargeSignInNag && !forceShowSmallSignInOnly && renderLargeNag()}
    </>
  );
}

export default (props) => <ComponentErrorBoundary><HomeSignInNag {...props} /></ComponentErrorBoundary>;
